import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Hourglass, ArrowRight } from "@phosphor-icons/react";

export default function Auth() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", nom_complet: "", profession: "", pays: "Mauritanie"
  });

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Dossier vitaliste ouvert");
      } else {
        await register(form);
        toast.success("Citoyenneté vitaliste enregistrée");
      }
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec d'authentification");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[var(--ink)] text-[var(--paper)] p-12 border-r border-black">
        <div>
          <div className="flex items-center gap-3">
            <Hourglass weight="fill" size={28} className="text-[var(--signal)]" />
            <div>
              <div className="font-serif text-2xl font-semibold">VITA-LEX</div>
              <div className="label-mono opacity-70">ANALYTICA · DOSSIER CITOYEN</div>
            </div>
          </div>
          <h2 className="mt-24 font-serif text-5xl leading-tight font-medium">
            Toute monnaie<br/>est un fragment<br/>de <span className="italic text-[var(--signal)]">vie cédée</span>.
          </h2>
          <p className="mt-8 text-[var(--paper)]/70 max-w-md leading-relaxed">
            Activez votre souveraineté vitaliste. Mesurez vos jours sacrifiés.
            Générez vos actes de reddition de comptes.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
          {[["V1","Sacrifice"],["D2","Hypothèque"],["SRV","Restitution"]].map(([k,v]) => (
            <div key={k} className="bg-[var(--ink)] p-5">
              <div className="font-serif text-2xl">{k}</div>
              <div className="label-mono opacity-70 mt-1">{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={handle} className="w-full max-w-md fade-up" data-testid="auth-form">
          <div className="label-mono">{mode === "login" ? "§ Accès dossier" : "§ Ouverture dossier"}</div>
          <h1 className="mt-3 font-serif text-4xl font-medium">{mode === "login" ? "Connexion" : "Inscription"}</h1>
          <p className="mt-2 text-sm text-[var(--muted)]">
            {mode === "login" ? "Votre identifiant ouvre les pièces de votre dossier vitaliste." : "Trois informations suffisent à constituer votre dossier."}
          </p>

          <div className="mt-8 space-y-4">
            {mode === "register" && (
              <>
                <Field label="Nom complet" testid="auth-nom">
                  <input required value={form.nom_complet} onChange={(e) => setForm({...form, nom_complet: e.target.value})}
                    className="w-full border border-black px-4 py-3 bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--signal)]"
                    placeholder="Mamadou Diallo" data-testid="auth-input-nom" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Profession" testid="auth-profession">
                    <input value={form.profession} onChange={(e) => setForm({...form, profession: e.target.value})}
                      className="w-full border border-black px-4 py-3 bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--signal)]"
                      placeholder="Enseignant" data-testid="auth-input-profession" />
                  </Field>
                  <Field label="Pays" testid="auth-pays">
                    <input value={form.pays} onChange={(e) => setForm({...form, pays: e.target.value})}
                      className="w-full border border-black px-4 py-3 bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--signal)]"
                      data-testid="auth-input-pays" />
                  </Field>
                </div>
              </>
            )}
            <Field label="Email" testid="auth-email">
              <input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                className="w-full border border-black px-4 py-3 bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--signal)]"
                placeholder="citoyen@vita-lex.org" data-testid="auth-input-email" />
            </Field>
            <Field label="Mot de passe" testid="auth-password">
              <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}
                className="w-full border border-black px-4 py-3 bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--signal)]"
                placeholder="••••••••" data-testid="auth-input-password" />
            </Field>
          </div>

          <button type="submit" disabled={loading}
            className="mt-6 w-full bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] py-4 label-mono flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="auth-submit">
            {loading ? "Vérification..." : (mode === "login" ? "Ouvrir le dossier" : "Constituer le dossier")}
            <ArrowRight size={16} weight="bold" />
          </button>

          <div className="mt-6 text-center text-sm text-[var(--muted)]">
            {mode === "login" ? "Pas encore de dossier ? " : "Déjà un dossier ? "}
            <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="underline text-[var(--ink)] hover:text-[var(--signal)]" data-testid="auth-toggle-mode">
              {mode === "login" ? "Inscription" : "Connexion"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={`field-${testid}`}>
      <div className="label-mono mb-2">{label}</div>
      {children}
    </label>
  );
}
